import {
  Alert, Box, Chip, CircularProgress, Paper, Stack, Typography, Divider,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useQuery } from "@tanstack/react-query";
import { tenantsApi, type TenantSettingsDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

/**
 * Read-only tenant settings page. The headline value is the tax regime —
 * shown alongside identity (name/code) and a clear note that switching
 * regimes post-go-live is a Finance-ticketed migration, not a UI button.
 *
 * Path: /admin/tenant-settings (registered in App.tsx).
 */
export function TenantSettingsPage() {
  const q = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (q.error || !q.data) {
    return <Alert severity="error" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>;
  }

  const t = q.data;
  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" gutterBottom>Tenant settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tenant identity and regime configuration. Operational data (branches,
        warehouses, users, feature flags) is managed in the dedicated pages.
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">Identity</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ mt: 1 }}>
          <Field label="Name" value={t.name} />
          <Field label="Code" value={t.code} />
          <Field label="Status">
            <Chip size="small"
              color={t.isActive ? "success" : "default"}
              label={t.isActive ? "Active" : "Inactive"} />
          </Field>
          <Field label="Created" value={new Date(t.createdAt).toLocaleDateString()} />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Tax regime</Typography>
          <LockIcon fontSize="small" sx={{ color: "text.disabled" }} />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} alignItems="flex-start">
          <Stack>
            <RegimeChip regime={t.taxRegime} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {regimeDescription(t.taxRegime)}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <strong>Changing the tax regime is not a UI action.</strong> Switching after
            go-live requires migrating historical journal entries, tax-account balances,
            and customer/vendor tax identifiers. Open a Finance ticket and the migration
            runs out of band.
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
}

function Field({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {value !== undefined ? <Typography variant="body1">{value}</Typography> : children}
    </Stack>
  );
}

function RegimeChip({ regime }: { regime: TenantSettingsDto["taxRegime"] }) {
  const map = {
    Gst:  { label: "India GST",      color: "primary"   as const },
    Vat:  { label: "VAT",            color: "secondary" as const },
    None: { label: "No statutory tax", color: "default" as const },
  };
  const m = map[regime];
  return <Chip color={m.color} label={m.label} sx={{ fontWeight: 600 }} />;
}

function regimeDescription(regime: TenantSettingsDto["taxRegime"]): string {
  switch (regime) {
    case "Gst":
      return "CGST/SGST intra-state, IGST inter-state. GL accounts 1410 (input) / 2200 (output). Eligible for IRP e-invoice and e-way bill integrations.";
    case "Vat":
      return "Single-component VAT. GL accounts 1411 (input) / 2210 (output). Jurisdiction-specific filing wired per customer (UAE FTA, KSA ZATCA, UK HMRC, EU OSS).";
    case "None":
      return "Template tenant or jurisdiction without statutory tax. Invoices post sub-total and AR/AP only; no tax line is generated.";
  }
}
